import React, { useState, useRef } from "react";
import { FolderOpen, CheckCircle, XCircle } from "lucide-react";
import toastr from "toastr";

interface IUploadResult {
  success: boolean;
  totalRows: number;
  successCount: number;
  failedCount: number;
  errors: IUploadError[];
}

interface IUploadError {
  row: number;
  email: string;
  message: string;
}

interface Props {
  policy_id: string;
  onClose?: () => void;
}

const BulkUploadEmployees: React.FC<Props> = ({ policy_id, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<IUploadResult | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  // Raw text of the uploaded CSV — kept so we can rebuild a "failed rows" CSV
  // that matches what the user submitted (header + EMPLOYEE/DEPENDENT lines).
  const [csvText, setCsvText] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMounted = useRef(true);

  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleFileSelected = (file: File) => {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toastr.error("Please select a valid .csv file.");
      return;
    }
    setSelectedFile(file);
    setUploadResult(null);
    setParseErrors([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvText(text);
      const firstLine = text.split("\n")[0];
      // Strip BOM and quotes, then normalize — handles Excel UTF-8 CSV saves
      const headers = firstLine.split(",").map((h) => h.trim().toLowerCase().replace(/^\ufeff/, "").replace(/^"|"$/g, "").replace(/[\s*]/g, ""));
      const required = ["type", "firstname", "lastname", "email", "dateofbirth", "benefitcategoryname"];
      const missing = required.filter((r) => !headers.includes(r));
      if (missing.length > 0) {
        setParseErrors([`Missing required columns: ${missing.join(", ")}`]);
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("policyId", policy_id);

      const response = await fetch("/api/Employees/bulkupload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload error response:", errorText);
        let parsed: Partial<IUploadResult> | null = null;
        try { parsed = JSON.parse(errorText); } catch { /* not JSON */ }

        if (parsed && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
          setUploadResult({
            success: false,
            totalRows: parsed.totalRows ?? 0,
            successCount: 0,
            failedCount: parsed.errors.length,
            errors: parsed.errors,
          });
        } else {
          setUploadResult({
            success: false,
            totalRows: 0,
            successCount: 0,
            failedCount: 1,
            errors: [{ row: 0, email: "", message: parsed?.["message"] ?? errorText }],
          });
        }
        return;
      }
      const result: IUploadResult = await response.json();
      result.errors = result.errors ?? [];

      if (!isMounted.current) return;

      setUploadResult(result);

      // Never auto-close — user must click Close button
    } catch {
      if (!isMounted.current) return;
      setUploadResult({
        success: false,
        totalRows: 0,
        successCount: 0,
        failedCount: 1,
        errors: [{ row: 0, email: "", message: "An unexpected error occurred during upload. Please try again." }],
      });
    } finally {
      if (!isMounted.current) return;
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "Type*", "Relation", "First Name*", "Last Name*", "Email*", "Date of Birth*", "Hire Date",
      "Benefit Category Name*", "Waiting Period Months", "Former Last Name", "Nickname",
      "Employee ID", "Has Prorating", "Salary",
      "Student", "Student Start Date", "Student End Date",
    ].join(",");

    const exampleRows = [
      "EMPLOYEE,,Jane,Doe,jane.doe@example.com,1990-05-15,2024-01-01,Standard,0,,,EMP001,false,65000,,,",
      "DEPENDENT,CHILD,Alice,Doe,,2015-03-20,,,,,,,,,,,",
      "DEPENDENT,CHILD,Bob,Doe,,2004-07-11,,,,,,,,,Student,2024-09-01,2026-04-30",
      "EMPLOYEE,,John,Smith,john.smith@example.com,1985-11-22,2024-02-01,Premium,3,,,EMP002,true,85000,,,",
      "DEPENDENT,SPOUSE,Sarah,Smith,,1987-04-10,,,,,,,,,,,",
    ].join("\n");

    const csv = `${headers}\n${exampleRows}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "employee_upload_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Builds a CSV of failed EMPLOYEE groups (employee row + its DEPENDENT rows)
  // with an appended "Error" column on the employee row, so the user can
  // correct issues in Excel and re-upload.
  const handleDownloadErrors = () => {
    if (!uploadResult || uploadResult.errors.length === 0 || !csvText) return;

    const lines = csvText.replace(/^﻿/, "").split(/\r?\n/);
    if (lines.length === 0) return;

    const escape = (v: string) =>
      v == null ? "" : /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;

    // Multiple errors can share a row number — join their messages.
    const messagesByRow = new Map<number, string[]>();
    for (const e of uploadResult.errors) {
      if (!messagesByRow.has(e.row)) messagesByRow.set(e.row, []);
      messagesByRow.get(e.row)!.push(e.message);
    }

    const failedRows = Array.from(messagesByRow.keys()).sort((a, b) => a - b);
    const out: string[] = [`${lines[0]},Error`];

    for (const rowNum of failedRows) {
      // Backend row numbering: header = row 1, first data row = row 2.
      const empIdx = rowNum - 1;
      if (empIdx <= 0 || empIdx >= lines.length) continue;

      const errMsg = messagesByRow.get(rowNum)!.join("; ");
      out.push(`${lines[empIdx]},${escape(errMsg)}`);

      // Walk forward to capture this employee's DEPENDENT rows; stop at the
      // next EMPLOYEE row or end of file.
      for (let i = empIdx + 1; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (!trimmed) continue;
        const firstField = trimmed.split(",")[0].trim().toUpperCase().replace(/^"|"$/g, "");
        if (firstField === "EMPLOYEE") break;
        if (firstField === "DEPENDENT") out.push(`${lines[i]},`);
      }
    }

    // BOM so Excel renders accented characters correctly.
    const blob = new Blob(["﻿" + out.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "employee_upload_errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const successfulEmployees = uploadResult
    ? uploadResult.totalRows - uploadResult.failedCount
    : 0;

  const failedEmails = uploadResult?.errors.map((e) => e.email).filter(Boolean) ?? [];

  return (
    <div style={{ padding: "24px", maxWidth: 860, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: 0 }}>Bulk Upload Employees</h4>
      </div>

      {/* Instructions */}
      <div style={{ background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 6, padding: "16px", marginBottom: 20, fontSize: 14, color: "#555", lineHeight: 1.6 }}>
        <p style={{ margin: "0 0 8px 0" }}>
          Upload a CSV file to bulk-add employees and their dependents. Each row must start with a <strong>Type</strong> column — either <code>EMPLOYEE</code> or <code>DEPENDENT</code>.
        </p>
        <p style={{ margin: "0 0 8px 0", fontStyle: "italic" }}>
          Headers marked with <code>*</code> are required. Some apply to every row, others only to a specific row type — see EMPLOYEE / DEPENDENT sections below.
        </p>
        <p style={{ margin: "0 0 4px 0" }}>
          <strong>EMPLOYEE required:</strong> First Name, Last Name, Email, Date of Birth, Benefit Category Name
        </p>
        <p style={{ margin: "0 0 4px 0" }}>
          <strong>EMPLOYEE optional:</strong> Hire Date, Waiting Period Months, Former Last Name, Nickname, Employee ID, Has Prorating, Salary
        </p>
        <p style={{ margin: "0 0 4px 0" }}>
          <strong>DEPENDENT:</strong> Add as many rows as needed after an EMPLOYEE row. Required: First Name, Last Name, Date of Birth, Relation (SPOUSE / CHILD / UNCOND)
        </p>
        <p style={{ margin: 0 }}>
          <strong>DEPENDENT student fields (informational, not yet processed on upload):</strong> Student (set to <code>Student</code> for post-secondary children, otherwise blank), Student Start Date, Student End Date
        </p>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) handleFileSelected(file);
        }}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${isDragging ? "#007bff" : "#ccc"}`,
          borderRadius: 8,
          padding: "40px 24px",
          textAlign: "center",
          background: isDragging ? "#f0f7ff" : "#fafafa",
          cursor: "pointer",
          transition: "all 0.2s",
          marginBottom: 16,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelected(file);
          }}
        />
        <FolderOpen size={40} style={{ display: "block", margin: "0 auto 8px", color: "#888" }} />
        {selectedFile ? (
          <div>
            <div style={{ fontWeight: 600, color: "#333" }}>{selectedFile.name}</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
              {(selectedFile.size / 1024).toFixed(1)} KB —{" "}
              <span
                style={{ color: "#dc3545", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                  setUploadResult(null);
                  setParseErrors([]);
                  setCsvText("");
                }}
              >
                Remove
              </span>
            </div>
          </div>
        ) : (
          <div style={{ color: "#888" }}>Drag &amp; drop a CSV file here, or click to browse</div>
        )}
      </div>

      {/* Parse Warnings */}
      {parseErrors.length > 0 && (
        <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 4, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>
          <strong>File validation warnings:</strong>
          <ul style={{ margin: "6px 0 0 0", paddingLeft: 20 }}>
            {parseErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Results */}
      {uploadResult && (
        <div style={{ border: "1px solid #dee2e6", borderRadius: 6, padding: 16, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 15 }}>Upload Results</div>

          {/* No-rows-uploaded banner */}
          {uploadResult.successCount === 0 && uploadResult.failedCount > 0 && (
            <div style={{ background: "#f8d7da", border: "1px solid #f5c6cb", borderRadius: 4, padding: "10px 14px", color: "#721c24", fontSize: 14, marginBottom: 12 }}>
              <XCircle size={16} style={{ marginRight: 6, verticalAlign: "middle" }} /> No employee data was uploaded. Please correct errors and try again.
            </div>
          )}

          {/* Summary cards */}
          <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
            {[
              { label: "Total Employees", value: uploadResult.totalRows,    color: "#333"    },
              { label: "Succeeded",       value: uploadResult.successCount, color: "#28a745" },
              { label: "Failed",          value: uploadResult.failedCount,  color: uploadResult.failedCount > 0 ? "#dc3545" : "#28a745" },
            ].map((s) => (
              <div key={s.label} style={{ textAlign: "center", background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 6, padding: "12px 24px" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: "#666" }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* All success */}
          {uploadResult.failedCount === 0 && (
            <div style={{ background: "#d4edda", border: "1px solid #c3e6cb", borderRadius: 4, padding: "10px 14px", color: "#155724", fontSize: 14, marginBottom: 12 }}>
              <CheckCircle size={16} style={{ marginRight: 6, verticalAlign: "middle" }} /> All employees were uploaded successfully.
            </div>
          )}

          {/* Failed employees table */}
          {uploadResult.errors.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontWeight: 600, color: "#dc3545", fontSize: 14 }}>
                  <XCircle size={16} style={{ marginRight: 6, verticalAlign: "middle" }} /> Failed Employees ({uploadResult.failedCount})
                </div>
                {csvText && (
                  <button
                    onClick={handleDownloadErrors}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 4,
                      border: "1px solid #dc3545",
                      background: "#fff",
                      color: "#dc3545",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    Download List of Errors
                  </button>
                )}
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8d7da" }}>
                    {["Row #", "Email", "Reason"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", border: "1px solid #f5c6cb", color: "#721c24" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {uploadResult.errors.map((err, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fff5f5" }}>
                      <td style={{ padding: "6px 10px", border: "1px solid #f5c6cb" }}>{err.row}</td>
                      <td style={{ padding: "6px 10px", border: "1px solid #f5c6cb" }}>{err.email || "—"}</td>
                      <td style={{ padding: "6px 10px", border: "1px solid #f5c6cb", color: "#dc3545" }}>{err.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Successfully inserted employees */}
          {uploadResult.successCount > 0 && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 8, color: "#28a745", fontSize: 14 }}>
                <CheckCircle size={16} style={{ marginRight: 6, verticalAlign: "middle" }} /> Successfully Uploaded Employees ({uploadResult.successCount})
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#d4edda" }}>
                    {["#", "Email"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", border: "1px solid #c3e6cb", color: "#155724" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={2} style={{ padding: "10px", border: "1px solid #c3e6cb", color: "#155724", fontStyle: "italic" }}>
                      {uploadResult.successCount} employee(s) were successfully added to the system.
                      {uploadResult.failedCount > 0 && " The failed ones are listed above."}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Footer Buttons */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button
          onClick={handleDownloadTemplate}
          style={{ padding: "8px 20px", borderRadius: 4, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}
        >
          Download Template
        </button>
        <button
          onClick={handleSubmit}
          disabled={!selectedFile || isUploading || parseErrors.length > 0}
          style={{
            padding: "8px 20px", borderRadius: 4, border: "none",
            background: !selectedFile || isUploading || parseErrors.length > 0 ? "#aaa" : "#007bff",
            color: "#fff", cursor: !selectedFile || isUploading || parseErrors.length > 0 ? "not-allowed" : "pointer",
          }}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </button>
        {onClose && (
          <button
            onClick={onClose}
            style={{ padding: "8px 20px", borderRadius: 4, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default BulkUploadEmployees;
